import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import AppColors from 'general/constants/AppColors';
import StringUtils from 'general/utils/StringUtils';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import KTFormSearch from '../Forms/KTFormSearch';

export const KTBSDropdownDirections = {
  left: 'dropleft',
  up: 'dropup',
  right: 'dropright',
  down: 'dropdown',
};

export const KTBSDropdownAlignments = {
  start: 'dropdown-menu-start',
  end: 'dropdown-menu-end',
};

export const KTBSDropdowAutoCloseBehavior = {
  true: 'true',
  false: 'false',
  inside: 'inside',
  outside: 'outside',
};

KTBSDropdown.propTypes = {
  name: PropTypes.string,
  toggleWrapperClassName: PropTypes.string,
  toggleElement: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  dropdownMenuClassName: PropTypes.string,
  dropdownMenuStyle: PropTypes.object,
  dropdownMenuItems: PropTypes.array,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  direction: PropTypes.oneOf(Object.values(KTBSDropdownDirections)),
  alignment: PropTypes.oneOf(Object.values(KTBSDropdownAlignments)),
  offset: PropTypes.object,
  staticDisplay: PropTypes.bool, // on | off dynamic positioning
  autoCloseBehavior: PropTypes.oneOf(Object.values(KTBSDropdowAutoCloseBehavior)),
  contentEl: PropTypes.element,
  onChange: PropTypes.func,
  text: PropTypes.string,

  enableCheckValid: PropTypes.bool,
  isTouched: PropTypes.bool,
  isValid: PropTypes.bool,
  feedbackText: PropTypes.string,

  searchable: PropTypes.bool,
  footerEl: PropTypes.element,

  inputCheckbox: PropTypes.func,
  handleItemClick: PropTypes.func,

  disabled: PropTypes.bool,
  isDropdownForm: PropTypes.bool,
  id: PropTypes.string,

  liveSearch: PropTypes.bool,
  onTextChanged: PropTypes.func,

  onUnCheck: PropTypes.func,
  optionIcon: PropTypes.element,
  defaultOpen: PropTypes.bool,
};

KTBSDropdown.defaultProps = {
  name: '',
  toggleWrapperClassName: '',
  toggleElement: <></>,
  dropdownMenuClassName: '',
  dropdownMenuStyle: {},
  dropdownMenuItems: [],
  selectedValue: null,
  direction: KTBSDropdownDirections.down,
  alignment: KTBSDropdownAlignments.start,
  offset: null,
  staticDisplay: true,
  autoCloseBehavior: KTBSDropdowAutoCloseBehavior.true,
  contentEl: null,
  onChange: null,
  text: '',

  enableCheckValid: false,
  isValid: true,
  isTouched: false,
  feedbackText: '',
  searchable: false,
  footerEl: null,
  inputCheckbox: null,
  handleItemClick: null,
  disabled: false,
  isDropdownForm: false,
  id: '',
  liveSearch: false,
  onTextChanged: null,
  onUnCheck: null,
  optionIcon: null,
  defaultOpen: false,
};

/**
 *
 * @param {{
 * name: string,
 * toggleWrapperClassName: string,
 * toggleElement: element|function,
 * dropdownMenuClassName: string,
 * dropdownMenuStyle: object,
 * dropdownMenuItems: object[],
 * selectedValue: string|number|object,
 * direction: string,
 * alignment: string,
 * offset: {},
 * staticDisplay: boolean,
 * autoCloseBehavior: boolean,
 * contentEl: element,
 * onChange: function,
 * text: string,
 *
 * enableCheckValid: boolean,
 * isValid: boolean,
 * isTouched: boolean,
 * feedbackText: string,
 *
 * searchable: boolean,
 * footerEl: element,
 * inputCheckbox:func,
 * handleItemClick:func,
 * disabled:boolean,
 * isDropdownForm:boolean,
 * id: string,
 *
 * liveSearch: boolean,
 * onTextChanged: function,
 *
 * onUnCheck: function,
 * optionIcon: element,
 * defaultOpen: boolean,
 *
 * }} props
 * @returns
 */
function KTBSDropdown(props) {
  // MARK: --- Params ---
  const {
    name,
    toggleWrapperClassName,
    toggleElement,
    dropdownMenuClassName,
    dropdownMenuStyle,
    dropdownMenuItems,
    selectedValue,
    direction,
    alignment,
    offset,
    staticDisplay,
    autoCloseBehavior,
    contentEl,
    onChange,
    text,

    enableCheckValid,
    isValid,
    isTouched,
    feedbackText,

    searchable,
    footerEl,
    inputCheckbox,
    handleItemClick,
    disabled,
    isDropdownForm,
    searchFormClassName,
    isOpen,
    id,
    liveSearch,
    onTextChanged,
    onUnCheck,
    optionIcon,
    defaultOpen,
    isPositionFixed = false,
  } = props;
  const currentSelectedValue = useRef(selectedValue);
  const showError = enableCheckValid && !isValid && isTouched && !_.isEmpty(feedbackText);
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();
  const [filteredDropdownMenuItems, setFilteredDropdownMenuItems] = useState([]);

  const toggleRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let filteredOptions = dropdownMenuItems;
    if (searchable) {
      filteredOptions = _.filter(dropdownMenuItems, (item) => {
        const itemName = StringUtils.removeVietnameseTones(item?.name ?? '').toLowerCase();
        return itemName?.includes(
          StringUtils.removeVietnameseTones(searchText ?? '').toLowerCase()
        );
      });
    }
    setFilteredDropdownMenuItems(filteredOptions);
  }, [searchText, dropdownMenuItems]);

  useEffect(() => {
    if (!isPositionFixed) return;
    if (!toggleRef.current) return;

    const toggleEl = toggleRef.current;

    const handleShow = () => {
      setTimeout(() => {
        updateDropdownPosition();
      }, 10);
    };

    toggleEl.addEventListener('shown.bs.dropdown', handleShow);

    const handleUpdate = () => requestAnimationFrame(updateDropdownPosition);
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      toggleEl.removeEventListener('shown.bs.dropdown', handleShow);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isPositionFixed]);

  const updateDropdownPosition = () => {
    if (!isPositionFixed) return;
    if (!toggleRef.current || !dropdownRef.current) return;

    const rect = toggleRef.current.getBoundingClientRect();
    const menu = dropdownRef.current;

    const spacing = 4;
    const viewportHeight = window.innerHeight;

    let top = rect.bottom + spacing;

    const dropdownHeight = menu.offsetHeight || 300;
    if (top + dropdownHeight > viewportHeight) {
      top = rect.top - dropdownHeight - spacing;
    }

    menu.style.top = `${top}px`;
    menu.style.left = `${rect.left}px`;
    menu.style.width = `${rect.width}px`;
  };

  return (
    <div id={`${id}`} className={`${!isDropdownForm ? 'd-flex' : ''}`}>
      <div className={`${direction} w-100 ${disabled && 'dropdown-disabled'}`}>
        {/* Toggle element */}
        {staticDisplay ? (
          <div
            ref={toggleRef}
            className={`${toggleWrapperClassName} ${direction}`}
            data-bs-toggle="dropdown"
            data-bs-display="static"
            aria-expanded={defaultOpen ? 'true' : 'false'}
            data-bs-auto-close={autoCloseBehavior}
          >
            {typeof toggleElement === 'function' ? toggleElement({ showError }) : toggleElement}
          </div>
        ) : (
          <div
            ref={toggleRef}
            className={`${toggleWrapperClassName} ${direction}`}
            data-bs-toggle="dropdown"
            aria-expanded={defaultOpen ? 'true' : 'false'}
            data-bs-auto-close={autoCloseBehavior}
          >
            {typeof toggleElement === 'function' ? toggleElement({ showError }) : toggleElement}
          </div>
        )}

        {/* Dropdown menu */}
        <div
          ref={dropdownRef}
          className={`dropdown-menu ${dropdownMenuClassName} ${alignment} py-0 ${defaultOpen ? 'show' : ''}`}
          style={{
            ...dropdownMenuStyle,
            display: disabled ? 'none' : '',
            position: isPositionFixed ? 'fixed' : undefined,
            top: isPositionFixed ? 0 : undefined,
            left: isPositionFixed ? 0 : undefined,
            zIndex: isPositionFixed ? 100 : undefined,
          }}
        >
          {searchable && (
            <div className={`${searchFormClassName ? searchFormClassName : 'px-4 pt-2'}`}>
              <KTFormSearch
                name={`${name}_searchbar`}
                value={searchText}
                placeholder={`${t('TypeToSearch')}...`}
                onSubmit={(text) => {
                  if (!liveSearch) {
                    setSearchText(text);
                  } else {
                    if (onTextChanged) {
                      onTextChanged(text);
                    }
                  }
                }}
              />
            </div>
          )}

          {!_.isEmpty(contentEl) ? (
            <div>{contentEl}</div>
          ) : (
            <div className={`${selectedValue && onUnCheck ? 'pt-2' : 'py-2'}`}>
              {filteredDropdownMenuItems.length > 0 ? (
                filteredDropdownMenuItems.map((item, index) => (
                  <div key={index}>
                    {inputCheckbox ? (
                      <div className="d-flex align-items-center ml-7">
                        {inputCheckbox(item)}
                        <a
                          className={`dropdown-item ${selectedValue == item.value ? 'active' : ''
                            } px-5`}
                          style={{ paddingLeft: 'unset' }}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleItemClick(item.value);
                          }}
                        >
                          {item.name}
                        </a>
                      </div>
                    ) : (
                      <a
                        className={`d-flex align-items-center justify-content-between dropdown-item ${selectedValue == item.value ? 'active' : ''
                          } px-5`}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          currentSelectedValue.current = item.value;
                          if (onChange) {
                            onChange(currentSelectedValue.current);
                          }
                        }}
                      >
                        <div className="d-flex gap-2 w-100">
                          {item?.iconEl && <div>{item.iconEl}</div>}

                          <div className="d-flex flex-column justify-content-center flex-grow-1">
                            <div className="d-flex align-items-center w-100">
                              <div className="text-truncate"
                                style={{
                                  whiteSpace: 'break-spaces',
                                  overflowWrap: 'break-word',
                                }}
                              >{item.name}</div>
                              {optionIcon && (
                                <span className="ms-auto d-flex align-items-center">
                                  {optionIcon}
                                </span>
                              )}
                            </div>

                            {item?.subTitle && (
                              <span className="text-muted">{item.subTitle}</span>
                            )}
                          </div>
                        </div>
                        {selectedValue == item.value && (
                          <i className="fa-regular fa-check text-success" />
                        )}
                      </a>
                    )}
                    {item.showDivider && <div className="dropdown-divider"></div>}
                  </div>
                ))
              ) : (
                <div className="mx-auto">
                  <div className="d-flex flex-column align-items-center justify-content-center gap-4 h-100 py-3">
                    <img src={imgEmptyNoData} style={{ maxWidth: '150px', maxHeight: '150px' }} />
                    <div className="d-flex flex-column align-items-center gap-2">
                      <span
                        style={{
                          fontWeight: '700',
                          fontSize: Utils.convertToFontSize(14),
                          color: AppColors.gray[800],
                        }}
                      >
                        {'Chưa có dữ liệu hiển thị'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!_.isNil(selectedValue) && onUnCheck && (
            <div
              className={`py-2 px-4 d-flex justify-content-end`}
              style={{
                position: 'sticky',
                bottom: 0,
                background: 'white',
                zIndex: 10,
                width: '100%',
              }}
            >
              <Button
                variant="secondary"
                className="fw-bold"
                style={{
                  fontSize: '13px',
                  color: AppColors.gray[800],
                  padding: '8px 16px',
                }}
                onClick={onUnCheck}
              >
                {t('Bỏ chọn')}
              </Button>
            </div>
          )}

          {/* footer element */}
          {footerEl && footerEl}
        </div>
      </div>
      {!_.isEmpty(text) && <span className="form-text text-muted">{text}</span>}
    </div>
  );
}

export default KTBSDropdown;
